'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useRoomContext, useVoiceAssistant } from '@livekit/components-react';
import { ActionBar } from '@/components/embed-popup/action-bar';
import { AudioVisualizer } from '@/components/embed-popup/audio-visualizer';
import { Transcript } from '@/components/embed-popup/transcript';
import useChatAndTranscription from '@/hooks/use-chat-and-transcription';
import { useDebugMode } from '@/hooks/useDebug';
import { cn } from '@/lib/utils';
const TILE_TRANSITION = {
    type: 'spring',
    stiffness: 675,
    damping: 75,
    mass: 1,
};
const TranscriptMotion = motion.create(Transcript);
function isAgentAvailable(agentState) {
    return agentState == 'listening' || agentState == 'thinking' || agentState == 'speaking';
}
export const PopupView = (_a) => {
    var { appConfig, disabled, sessionStarted, onEmbedError, className = "", ref } = _a, props = __rest(_a, ["appConfig", "disabled", "sessionStarted", "onEmbedError", "className", "ref"]);
    if (process.env.NODE_ENV === "development") {
        useDebugMode();
    }
    const room = useRoomContext();
    const { state: agentState, audioTrack: agentAudioTrack } = useVoiceAssistant();
    const [chatOpen, setChatOpen] = useState(false);
    const { messages, send } = useChatAndTranscription();
    const { supportsChatInput, supportsAudioInput } = appConfig;
    const capabilities = {
        supportsChatInput,
        supportsVideoInput: false,
        supportsScreenShare: false,
    };
    const controls = {
        microphone: supportsAudioInput,
    };
    async function onSendMessage(message) {
        await send(message);
        return;
    }
    // Auto-open chat when agent becomes available
    useEffect(() => {
        if (sessionStarted && isAgentAvailable(agentState) && !chatOpen) {
            setChatOpen(true);
        }
    }, [agentState, sessionStarted, chatOpen]);
    // If the agent hasn't connected after an interval,
    // then show an error - something must not be working
    useEffect(() => {
        if (!sessionStarted) {
            return;
        }
        const timeout = setTimeout(() => {
            if (!isAgentAvailable(agentState)) {
                const reason = agentState === 'connecting'
                    ? 'El asistente no pudo unirse a la conversación. '
                    : 'El asistente se conectó pero no completó la inicialización. ';
                onEmbedError({
                    title: 'Sesión finalizada',
                    description: _jsx("p", { className: "w-full", children: reason }),
                });
            }
        }, 10000);
        return () => clearTimeout(timeout);
    }, [agentState, sessionStarted, room, onEmbedError]);
    return (_jsx("div", { ref: ref, inert: disabled, className: "flex h-full w-full flex-col overflow-hidden", children: _jsxs("div", { className: "relative flex h-full shrink-1 grow-1 flex-col", children: [_jsx(TranscriptMotion, { initial: {
                        y: 10,
                        opacity: 0,
                    }, animate: {
                        y: chatOpen ? 0 : 10,
                        opacity: chatOpen ? 1 : 0,
                    }, transition: {
                        type: 'spring',
                        duration: 0.5,
                        bounce: 0,
                    }, messages: messages }), _jsx(motion.div, { initial: {
                        scale: 1,
                        left: '50%',
                        top: '50%',
                        translateX: '-50%',
                        translateY: '-50%',
                        transformOrigin: 'center top',
                    }, animate: {
                        left: '50%',
                        scale: chatOpen ? 0.275 : 1,
                        top: chatOpen ? '12px' : '50%',
                        translateY: chatOpen ? '0' : '-50%',
                        transformOrigin: chatOpen ? 'center top' : 'center center',
                    }, transition: TILE_TRANSITION, className: cn('bg-bg1 dark:bg-bg2 pointer-events-none absolute flex aspect-square w-64 items-center justify-center rounded-2xl border border-transparent transition-colors', chatOpen && 'border-separator1 dark:border-separator2 drop-shadow-2xl'), children: _jsx(AudioVisualizer, { agentState: agentState, audioTrack: agentAudioTrack }) }, "audio-visualizer"), _jsx(motion.div, { initial: {
                        opacity: 0,
                        translateY: 8,
                    }, animate: {
                        opacity: sessionStarted ? 1 : 0,
                        translateY: sessionStarted ? 0 : 8,
                    }, transition: {
                        delay: 0.5,
                    }, children: _jsx(ActionBar, { capabilities: capabilities, controls: controls, onSendMessage: onSendMessage, onChatOpenChange: setChatOpen }) })] }) }));
};
